import { useState, useEffect, useCallback, useRef } from "react"
import {
  fetchPipelines,
  createPipeline,
  updatePipeline,
  deletePipeline,
  activatePipeline,
  createStage,
  updateStage,
  deleteStage,
  reorderStages,
} from "../api/pipelinesApi"

export function usePipelines(entityType) {
  const [pipelines, setPipelines] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  const load = useCallback(async () => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    setError(null)
    try {
      const data = await fetchPipelines(entityType)
      if (!ctrl.signal.aborted) setPipelines(data)
    } catch (err) {
      if (!ctrl.signal.aborted) setError(err.message || "Failed to load pipelines")
    } finally {
      if (!ctrl.signal.aborted) setLoading(false)
    }
  }, [entityType])

  useEffect(() => { load() }, [load])
  useEffect(() => () => abortRef.current?.abort(), [])

  return { pipelines, loading, error, reload: load }
}

function useMutation(fn) {
  const [isPending, setPending] = useState(false)
  const mutate = useCallback(
    async (...args) => {
      setPending(true)
      try {
        return await fn(...args)
      } catch (err) {
        throw err.response?.data?.errors || [err.message || "Something went wrong"]
      } finally {
        setPending(false)
      }
    },
    [fn]
  )
  return { mutate, isPending }
}

export const useCreatePipeline   = () => useMutation(createPipeline)
export const useUpdatePipeline   = () => useMutation(updatePipeline)
export const useDeletePipeline   = () => useMutation(deletePipeline)
export const useActivatePipeline = () => useMutation(activatePipeline)
export const useCreateStage      = () => useMutation(createStage)
export const useUpdateStage      = () => useMutation(updateStage)
export const useDeleteStage      = () => useMutation(deleteStage)
export const useReorderStages    = () => useMutation(reorderStages)
